import { captureEvent } from './analytics'

const API_BASE = import.meta.env.VITE_API_URL || ''

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...options?.headers as Record<string, string> }
  if (options?.body) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers,
    ...options,
  })

  if (res.status === 401) {
    throw new Error('Not authenticated')
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(err.message || err.error || `Request failed: ${res.status}`)
  }

  return res.json()
}

export const api = {
  getMe: () => request<{ user: User }>('/api/me'),
  // Analytics fires here (not at call sites) so every entry point — onboarding
  // and the channels page — is covered without duplicating the capture. The
  // activation funnel keys on `subscriptions_imported`.
  importSubscriptions: async () => {
    const res = await request<{ channels: Channel[]; count: number }>('/api/youtube/import-subscriptions', { method: 'POST' })
    captureEvent('subscriptions_imported', { count: res.count })
    return res
  },
  syncRecent: () => request<{ synced: number; channels: number }>('/api/youtube/sync-recent', { method: 'POST' }),
  getChannels: (params?: { page?: number; pageSize?: number; q?: string; sort?: 'title' | 'frequency_desc'; filter?: 'all' | 'followed' }) => {
    const qs = new URLSearchParams()
    if (params?.page) qs.set('page', String(params.page))
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
    if (params?.q) qs.set('q', params.q)
    if (params?.sort) qs.set('sort', params.sort)
    if (params?.filter) qs.set('filter', params.filter)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<ChannelsResponse>(`/api/channels${suffix}`)
  },
  getFollowedChannelIds: () => request<{ ids: string[] }>('/api/channels/followed-ids'),
  lookupChannels: (ids: string[]) => request<{ channels: Channel[] }>('/api/channels/lookup', { method: 'POST', body: JSON.stringify({ ids }) }),
  getFrequencySummary: () => request<FrequencySummaryResponse>('/api/channels/frequency-summary'),
  // Covers all follow entry points (onboarding batch, channels page, inline
  // feed CTA). `count` lets us tell a batch follow from a single ad-hoc one.
  // Only fires when something was actually followed.
  followChannels: async (channelIds: string[]) => {
    const res = await request('/api/channels/follow', { method: 'POST', body: JSON.stringify({ channelIds }) })
    if (channelIds.length > 0) captureEvent('channel_followed', { count: channelIds.length })
    return res
  },
  unfollowChannels: (channelIds: string[]) => request('/api/channels/unfollow', { method: 'POST', body: JSON.stringify({ channelIds }) }),
  // Self-reported intent from onboarding step 0 ('creator' | 'viewer').
  setSegment: async (segment: 'creator' | 'viewer') => {
    const res = await request<{ ok: boolean; segment: string }>(
      '/api/me/segment',
      { method: 'POST', body: JSON.stringify({ segment }) }
    )
    captureEvent('onboarding_segment_selected', { segment })
    return res
  },
  // Describe-your-niche discovery: LLM + YouTube propose channels to track.
  // Returns upserted channels (with DB ids) the user can then followChannels().
  discoverNiche: async (description: string) => {
    const res = await request<{ channels: DiscoveredChannel[] }>(
      '/api/niche/discover',
      { method: 'POST', body: JSON.stringify({ description }) }
    )
    captureEvent('niche_discovery_run', { count: res.channels.length })
    return res
  },
  // Add (and follow) any channel by URL / @handle — including channels the user
  // doesn't subscribe to. Powers competitor tracking.
  addChannelByUrl: async (input: string) => {
    const res = await request<{ channel: { id: string; youtube_channel_id: string; title: string; thumbnail_url: string }; alreadyFollowed: boolean }>(
      '/api/channels/add-by-url',
      { method: 'POST', body: JSON.stringify({ input }) }
    )
    if (!res.alreadyFollowed) captureEvent('channel_added_by_url', {})
    return res
  },
  getFeed: (window?: string) => request<FeedResponse>(`/api/feed${window ? `?window=${window}` : ''}`),
  // Latest brief by default; pass a runId to load a specific past brief (archive).
  getBriefing: (runId?: string) => request<BriefingResponse>(`/api/briefing${runId ? `?runId=${runId}` : ''}`),
  getBriefingHistory: (days?: number) => request<{ briefings: BriefingHistoryItem[] }>(`/api/briefings/history${days ? `?days=${days}` : ''}`),
  getBrowse: (window?: string) => request<BrowseResponse>(`/api/feed/browse${window ? `?window=${window}` : ''}`),
  getPopularChannels: (params?: { topic?: string }) => {
    const qs = new URLSearchParams()
    if (params?.topic) qs.set('topic', params.topic)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<PopularChannelsResponse>(`/api/browse/popular${suffix}`)
  },
  getBrowseTopics: () => request<{ topics: string[] }>('/api/browse/topics'),
  getDiscoverVideos: () => request<{ videos: DiscoverVideo[] }>('/api/browse/discover-videos'),
  // Popular-videos surface for zero-follow users. Same shape as Discover so
  // the existing DiscoverVideoCard renders it with inline follow CTAs.
  getPopularVideos: () => request<{ videos: DiscoverVideo[] }>('/api/browse/popular-videos'),
  getRecommendedChannels: () => request<{ channels: RecommendedChannel[] }>('/api/browse/recommended'),
  getChannelPreview: (channelId: string) =>
    request<{ channel: ChannelPreview; videos: FeedVideo[] }>(`/api/channels/${channelId}/preview`),
  getVideo: (id: string) => request<{ video: VideoDetail; relatedVideos: RelatedVideo[] }>(`/api/videos/${id}`),
  search: (q: string, topK = 5, mode: 'keyword' | 'hybrid' = 'hybrid') => {
    const qs = new URLSearchParams({ q, topK: String(topK), mode })
    return request<SearchResponse>(`/api/search?${qs.toString()}`)
  },
  favoriteVideo: (videoId: string) =>
    request<{ ok: boolean; favorited: boolean }>(`/api/videos/${videoId}/favorite`, { method: 'POST' }),
  // Post-signup: claim a video summarized while logged out (by youtube id) so
  // it's favorited + accessible in the new user's Library. Returns the internal id.
  claimVideo: (youtubeVideoId: string) =>
    request<{ videoId: string }>('/api/videos/claim', { method: 'POST', body: JSON.stringify({ youtubeVideoId }) }),
  unfavoriteVideo: (videoId: string) =>
    request<{ ok: boolean; favorited: boolean }>(`/api/videos/${videoId}/favorite`, { method: 'DELETE' }),
  // Ad-hoc "summarize any YouTube link" — queues a summary into our DB. Does not
  // follow the channel or touch the Library. `status` is 'processing' | 'ready'
  // | 'failed' ('ready' when we already had the video summarized).
  rundown: (url: string) =>
    request<{ youtubeVideoId: string; videoId?: string; status: string }>(
      '/api/rundown',
      { method: 'POST', body: JSON.stringify({ url }) },
    ),
  // "Share to Public" — Docs-style capability link for a single summary.
  getShareStatus: (videoId: string) =>
    request<ShareStatus>(`/api/videos/${videoId}/share`),
  createShare: (videoId: string) =>
    request<{ token: string; url: string }>(`/api/videos/${videoId}/share`, { method: 'POST' }),
  revokeShare: (videoId: string) =>
    request<{ ok: boolean; shared: boolean }>(`/api/videos/${videoId}/share`, { method: 'DELETE' }),
  // Draft a tweet from a summary (the "Share as tweet" growth loop). Returns the
  // draft text + the public /v/ back-link; the client opens the X composer.
  draftTweet: (videoId: string) =>
    request<{ text: string; url: string }>(`/api/videos/${videoId}/tweet-draft`, { method: 'POST' }),
  getLibrary: (params?: { tag?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams()
    if (params?.tag) qs.set('tag', params.tag)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<LibraryResponse>(`/api/library${suffix}`)
  },
  getSettings: () => request<{ settings: UserSettings }>('/api/settings'),
  updateSettings: (settings: Partial<UserSettings>) => request('/api/settings', { method: 'PUT', body: JSON.stringify(settings) }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  disconnect: () => request('/api/account/disconnect', { method: 'POST' }),
  createCheckout: (priceId: string) => request<{ customerId: string; priceId: string }>('/api/billing/checkout', { method: 'POST', body: JSON.stringify({ priceId }) }),
  createCreditsCheckout: (quantity: number) => request<{ customerId: string; priceId: string; quantity: number }>('/api/billing/checkout-credits', { method: 'POST', body: JSON.stringify({ quantity }) }),
  getBillingPortal: () => request<{ updatePaymentMethod: string | null; cancel: string | null }>('/api/billing/portal'),
}

export interface User {
  id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  timezone: string
  digest_enabled: boolean
  digest_hour_local: number
  billing_plan: 'free' | 'plus' | 'max'
  billing_plan_interval: string | null
  paddle_subscription_status: string | null
  billing_cycle_start: string
  billing_cycle_end: string
  min_video_duration_seconds: number
  credit_balance: number
  credits_available: number
  user_segment: 'creator' | 'viewer' | null
}



export interface Channel {
  id: string
  youtube_channel_id: string
  title: string
  description: string
  thumbnail_url: string
  is_followed?: boolean
  recent_upload_count: number | null
  frequency_window_days: number
  frequency_computed_at: string | null
  topics: string[] | null
}

export interface ChannelsResponse {
  channels: Channel[]
  total: number
  page: number
  pageSize: number
}

export interface DiscoveredChannel {
  id: string
  youtube_channel_id: string
  title: string
  thumbnail_url: string
  description: string
  subscriber_count: number | null
  already_followed: boolean
}

export interface FrequencySummaryEntry {
  id: string
  recent_upload_count: number | null
  frequency_window_days: number
  frequency_computed_at: string | null
}

export interface FrequencySummaryResponse {
  channels: FrequencySummaryEntry[]
  pending: number
}

export interface FeedVideo {
  id: string
  youtube_video_id: string
  title: string
  thumbnail_url: string
  youtube_url: string
  published_at: string
  created_at: string
  duration_seconds: number
  ingestion_status: string
  channel_name: string
  channel_thumbnail: string
  summary_status: string | null
  source_type: string | null
  confidence_level: string | null
  summary_short: string | null
  summary_long: string | null
  key_points_json: Array<{ text: string; timestamp_seconds: number } | string> | null
  worth_watching_if: string | null
  locked: boolean
  // Why this video is locked. 'quota' = user is over their monthly summary
  // limit (driven by /api/feed billing gate). 'preview' = user has no follow
  // record for this channel and this isn't the single allowed Browse-preview
  // video. null when not locked. Optional for backward compat with any
  // response shape that hasn't been updated yet.
  lock_reason?: 'quota' | 'preview' | null
  // Whether the current user has starred this video. Returned by /api/feed,
  // /api/library, and /api/videos/:id. Optional on response surfaces that
  // don't compute it yet (browse, channel preview, discover).
  is_favorited?: boolean
}

export interface ShareStatus {
  shared: boolean
  url: string | null
}

export interface FeedUsage {
  used: number
  // Effective allowance = plan_limit + credit_balance.
  limit: number
  plan_limit: number
  remaining: number
  plan: string
  // Videos in the user's followed channels that ended up skipped_no_quota
  // this billing cycle — surfaces an upgrade signal without blocking the feed.
  missed_this_cycle: number
  credit_balance: number
  credits_available: number
}

export interface FeedResponse {
  videos: FeedVideo[]
  window: string
  usage: FeedUsage
}

// "What's happening" — cross-video clustering briefing. Each cluster's videos
// reuse FeedVideo (so they render through VideoCard) plus a per-video takeaway.
export interface BriefingClusterVideo extends FeedVideo {
  unique_takeaway: string | null
}

export interface BriefingDisagreement {
  point: string
  channels: string[]
}

export interface BriefingCluster {
  id: string
  title: string
  common_ground: string | null
  disagreements: BriefingDisagreement[]
  videos: BriefingClusterVideo[]
}

export interface BriefingResponse {
  clusters: BriefingCluster[]
  generated_at: string | null
}

export interface BriefingHistoryItem {
  id: string
  created_at: string
  cluster_count: number
  source_video_count: number
  top_title: string | null
}

// Returned by /api/browse/discover-videos. Extends FeedVideo with channel_id
// so the discover card's "Follow channel" CTA has the id it needs.
export interface DiscoverVideo extends FeedVideo {
  channel_id: string
}

export interface PopularChannel {
  id: string
  youtube_channel_id: string
  title: string
  description: string
  thumbnail_url: string
  topics: string[] | null
  follower_count: number
}

export interface PopularChannelsResponse {
  channels: PopularChannel[]
}

// Returned by /api/channels/:id/preview. Header for the Browse preview drawer
// (channel info + topic chips); the videos field is a separate array of
// FeedVideo so it can render through the existing VideoCard.
export interface ChannelPreview {
  id: string
  youtube_channel_id: string
  title: string
  description: string | null
  thumbnail_url: string | null
  topics: string[] | null
}

// Returned by /api/browse/recommended. Drops follower_count (this list is
// ranked by user-overlap, not in-app popularity) and adds:
//   - overlap_users: how many peer users with similar tastes also follow this
//   - reason_channel: title of the user's followed channel that drives the
//     strongest co-follow link, used for "Because you follow X" hint
export interface RecommendedChannel {
  id: string
  youtube_channel_id: string
  title: string
  description: string
  thumbnail_url: string
  topics: string[] | null
  overlap_users: number
  reason_channel: string | null
}

export interface BrowseResponse {
  videos: FeedVideo[]
  window: string
}

export interface ClaimArgument {
  text: string
  timestamp_seconds: number
}

export interface Claim {
  topic: string
  claim: string
  supporting_arguments: ClaimArgument[]
}

// v2 summary-schema content type. The model classifies every new video into
// exactly one of these 11. Phase 1 of v2 only emits structured_extras for the
// first five (lecture, explainer, tech_review, debate_or_panel, cooking);
// the other six are classified for forward-compat but return null extras.
export type ContentType =
  | 'lecture'
  | 'explainer'
  | 'tutorial'
  | 'research_talk'
  | 'tech_review'
  | 'news_analysis'
  | 'interview'
  | 'debate_or_panel'
  | 'cooking'
  | 'news_brief'
  | 'entertainment'

export type ContentTypeConfidence = 'high' | 'medium' | 'low'

// Per-type structured payloads. Field shapes mirror the backend gemini.ts
// types one-to-one — see SUMMARY_SCHEMA_SPEC.md for the design rationale.

export interface TimestampedString { text: string; timestamp_seconds: number }

export interface LectureExtras {
  concepts_introduced: Array<{ term: string; plain_definition: string; timestamp_seconds: number }>
  prerequisites: string[]
  references: Array<{ title: string; kind: string; timestamp_seconds: number }>
  key_quotes: Array<{ quote: string; timestamp_seconds: number }>
}

export interface ExplainerExtras {
  central_question: string
  concepts_introduced: Array<{ term: string; plain_definition: string; timestamp_seconds: number }>
  takeaways: string[]
  references: Array<{ title: string; kind: string; timestamp_seconds: number }>
}

export interface TechReviewExtras {
  product: { name: string; category: string; price_mentioned?: string }
  verdict: string
  recommendation: 'buy' | 'wait' | 'skip' | 'depends' | 'no_clear_recommendation'
  pros: TimestampedString[]
  cons: TimestampedString[]
  specs: Array<{ attribute: string; value: string; timestamp_seconds: number }>
  compared_to: Array<{ name: string; verdict_vs: string }>
  best_for: string[]
  not_for: string[]
}

export interface DebateOrPanelExtras {
  participants: string[]
  topics_in_order: Array<{ topic: string; start_seconds: number; end_seconds: number }>
  positions: Array<{ speaker: string; position: string; on_topic: string; timestamp_seconds: number }>
  cruxes: Array<{ description: string; timestamp_seconds: number }>
  notable_quotes: Array<{ speaker: string; quote: string; timestamp_seconds: number }>
}

export interface CookingIngredient {
  name: string
  quantity?: string
  unit?: string
  preparation?: string
  group?: string
}

export interface CookingStep {
  n: number
  instruction: string
  timestamp_seconds: number
  tip?: string
  duration_seconds_in_video?: number
}

export interface CookingSubstitution {
  original: string
  substitute: string
  note?: string
}

export interface CookingExtras {
  recipe: {
    name: string
    cuisine?: string
    servings?: string
    prep_time_minutes?: number
    cook_time_minutes?: number
    total_time_minutes?: number
    difficulty?: 'easy' | 'medium' | 'hard'
    ingredients: CookingIngredient[]
    equipment: string[]
    steps: CookingStep[]
    finishing: string[]
    substitutions: CookingSubstitution[]
    storage?: string
    pairings?: string[]
  }
  notes_from_creator?: string[]
}

export interface TutorialStep {
  n: number
  title: string
  timestamp_seconds: number
  detail?: string
  gotcha?: string
}

export interface TutorialExtras {
  goal: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  prerequisites: string[]
  dependencies: string[]
  estimated_time_minutes?: number
  steps: TutorialStep[]
  outcome: string
}

export interface ResearchTalkExtras {
  research_question: string
  methods: string[]
  findings: Array<{ text: string; timestamp_seconds: number }>
  references: Array<{ title: string; kind: string; timestamp_seconds: number }>
  open_questions: string[]
  key_quotes: Array<{ quote: string; timestamp_seconds: number }>
}

export interface InterviewExtras {
  guest: { name: string; background: string }
  topics_in_order: Array<{ topic: string; start_seconds: number; end_seconds: number }>
  notable_quotes: Array<{ quote: string; timestamp_seconds: number }>
  recommendations_mentioned: Array<{ title: string; kind: string; timestamp_seconds: number }>
}

export interface NewsAnalysisKeyClaim {
  claim: string
  supporting_arguments: Array<{ text: string; timestamp_seconds: number }>
}

export interface NewsAnalysisExtras {
  thesis: string
  context: string
  key_claims: NewsAnalysisKeyClaim[]
  sources_cited: Array<{ description: string; timestamp_seconds: number }>
  broader_implications: string[]
}

export interface NewsBriefExtras {
  stories: Array<{ headline: string; summary: string; timestamp_seconds: number }>
}

// Discriminated union — caller switches on VideoDetail.content_type to narrow.
export type StructuredExtras =
  | LectureExtras
  | ExplainerExtras
  | TechReviewExtras
  | DebateOrPanelExtras
  | CookingExtras
  | TutorialExtras
  | ResearchTalkExtras
  | InterviewExtras
  | NewsAnalysisExtras
  | NewsBriefExtras

// Sibling of `video` on the /api/videos/:id response. A small set of
// videos from the user's reachable library (starred + active/inactive
// follows) that share at least one tag with the open video. Drives the
// "Related from your library" section on the detail page. Server returns
// an empty array when the source video has no tags or no matches exist.
export interface RelatedVideo {
  id: string
  youtube_video_id: string
  title: string
  thumbnail_url: string | null
  published_at: string
  duration_seconds: number | null
  channel_name: string
  channel_thumbnail: string | null
  is_favorited: boolean
  // True when the video's channel is one the user actively follows — lets the
  // single blended rail subtly mark "from a creator you follow".
  is_followed: boolean
  // Tags shared with the source video — the readable "why" behind a match
  // (embeddings do the ranking). May be empty when there's no literal overlap.
  shared_tags: string[]
}

export interface VideoDetail extends FeedVideo {
  description: string
  generation_model: string | null
  summary_completed_at: string | null
  // Open-vocabulary subject tags emitted by the summarizer. Always present
  // (defaults to []) for ready summaries; older rows may have an empty array
  // since tags ship after the existing 8K were generated.
  tags: string[]
  // Structured argument extraction. NULL when the content type doesn't
  // qualify (entertainment, tech reviews, cooking, etc.) OR when the row
  // predates the claims feature. Only argumentative content (education,
  // podcasts/debates, news analysis, history essays) gets this populated.
  claims_json: Claim[] | null
  // v2 summary-schema fields. All three are NULL for pre-v2 rows — the UI
  // falls back to the v1 layout in that case. content_type/confidence are
  // set on every new summary (any content_type, all 11 values); only the
  // Phase 1 five types carry a non-null structured_extras.
  content_type: ContentType | null
  content_type_confidence: ContentTypeConfidence | null
  structured_extras: StructuredExtras | null
}

// Library response. Returns the user's starred videos (same shape as feed
// for VideoCard reuse) plus a precomputed tag-counts list for the filter
// rail. Tag-counts are over the WHOLE library (not the filtered slice) so
// the rail keeps showing the user's full distribution while drilled in.
export interface LibraryTagCount {
  tag: string
  count: number
}

// Library rows carry tags so the row card can show subject chips. Other
// feed/browse responses don't include tags today (deferred to keep cards
// uncluttered); only Library renders them inline.
export interface LibraryVideo extends FeedVideo {
  tags: string[]
}

export interface LibraryResponse {
  videos: LibraryVideo[]
  tagCounts: LibraryTagCount[]
  total: number
  page: number
  pageSize: number
  tag: string | null
}

// Search response. `summarySnippet` contains `<mark>...</mark>` markers around
// matched tokens — the modal splits on those instead of using innerHTML.
export interface SearchHit {
  videoId: string
  youtubeVideoId: string
  title: string
  thumbnailUrl: string | null
  channelName: string
  channelThumbnail: string | null
  publishedAt: string
  durationSeconds: number | null
  summarySnippet: string | null
  rank: number
  isFollowed: boolean
}

export interface SearchResponse {
  results: SearchHit[]
  query: string
  topK: number
  mode?: 'keyword' | 'hybrid'
}

export interface UserSettings {
  timezone: string
  digest_enabled: boolean
  digest_hour_local: number
  min_video_duration_seconds: number
}
